import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { FlowMarkComponent } from '../../../../../flow-v2/identity/flow-mark.component';
import { PaperIconComponent, PaperKbdComponent } from '@ricoh-paper/angular';
import type { CmdkActionItem, CmdkNavItem } from '../../models/cmdk.model';

/**
 * The row for the two registry-backed kinds — navigation destinations and
 * actions. One component rather than two, because they render identically:
 * icon tile, title, subtitle, trailing shortcut keycap. What differs is what
 * activating them does, and that belongs to the palette, not to the row.
 *
 * AI CONTEXT — why this is a `<div role="option">` and not a button element
 *
 * Two reasons, and either alone would be enough.
 *
 * The a11y one: this is a LISTBOX. In the combobox + listbox pattern focus
 * never leaves the input; rows are `role="option"` and are pointed at by
 * `aria-activedescendant`. A focusable control inside a listbox is a focusable
 * element inside a container whose options must not be focusable, and screen
 * readers announce the nesting.
 *
 * The gate one: `polish-audit`'s `raw_buttons:features/shell` counter is at
 * its baseline with zero headroom (SPEC-command-k.md §7 R1). A palette that
 * renders one raw button element per result fails CI on its first render.
 */
@Component({
  selector: 'im-cmdk-row-command',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlowMarkComponent, PaperIconComponent, PaperKbdComponent],
  templateUrl: './cmdk-row-command.component.html',
  styleUrl: './cmdk-row-command.component.scss',
})
export class CmdkRowCommandComponent {
  readonly item = input.required<CmdkNavItem | CmdkActionItem>();
  readonly selected = input(false);
  /** The ⌘N badge to show while the modifier is held, or null. */
  readonly quickOpenIndex = input<number | null>(null);

  protected readonly icon = computed(() => this.item().command.icon);

  /** The digit shown while ⌘ is held, or null when this row is not numbered. */
  protected readonly quickOpenKey = computed(() => {
    const index = this.quickOpenIndex();
    return index === null ? null : String(index + 1);
  });

  /**
   * The registry writes shortcuts as `⌘K`. `paper-kbd` renders the platform
   * modifier itself from `[mod]`, so the glyph is stripped rather than
   * hardcoded — a literal ⌘ is wrong on every Windows machine.
   */
  protected readonly shortcutKeys = computed(() => {
    const raw = this.item().shortcut;
    return raw ? raw.replace('⌘', '') : null;
  });

  protected readonly usesModifier = computed(() => (this.item().shortcut ?? '').includes('⌘'));
}
